import { createContext, useContext, useState, useRef, useEffect, memo } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "reactflow";
import { Clock, Plus, Minus, Trash2, Smile, Shapes, X, Link2, FileText, Table2, Wallet, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export const MindMapContext = createContext(null);

const COLOR_STYLES = {
  green: "bg-emerald-50 dark:bg-emerald-950/50 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-100",
  red: "bg-rose-50 dark:bg-rose-950/50 border-rose-300 dark:border-rose-700 text-rose-900 dark:text-rose-100",
  neutral: "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100",
  sky: "bg-sky-100 dark:bg-sky-500/20 border-sky-400 text-sky-900 dark:text-sky-50",
  pink: "bg-pink-100 dark:bg-pink-500/20 border-pink-400 text-pink-900 dark:text-pink-50",
  amber: "bg-amber-100 dark:bg-amber-500/20 border-amber-400 text-amber-900 dark:text-amber-50",
  violet: "bg-violet-100 dark:bg-violet-500/20 border-violet-400 text-violet-900 dark:text-violet-50",
  lime: "bg-lime-100 dark:bg-lime-500/20 border-lime-500 text-lime-900 dark:text-lime-50",
  orange: "bg-orange-100 dark:bg-orange-500/20 border-orange-400 text-orange-900 dark:text-orange-50",
  teal: "bg-teal-100 dark:bg-teal-500/20 border-teal-400 text-teal-900 dark:text-teal-50",
  fuchsia: "bg-fuchsia-100 dark:bg-fuchsia-500/20 border-fuchsia-400 text-fuchsia-900 dark:text-fuchsia-50",
};

export const SHAPES = [
  { id: "rounded", label: "Cuadro" },
  { id: "pill", label: "Píldora" },
  { id: "circle", label: "Círculo" },
  { id: "oval", label: "Óvalo" },
  { id: "cloud", label: "Nube" },
];

const CLOUD_RADIUS = "47% 53% 70% 30% / 62% 46% 60% 40%";

const LINK_META = {
  note: { label: "Nota", Icon: FileText, cls: "text-sky-600 dark:text-sky-400 bg-sky-500/10 border-sky-500/30" },
  sheet: { label: "Hoja", Icon: Table2, cls: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  movemap: { label: "Movimientos", Icon: Wallet, cls: "text-orange-600 dark:text-orange-400 bg-orange-500/10 border-orange-500/30" },
};

const EMOJIS = [
  "😀","😃","😄","😁","😆","😅","😂","🙂","😊","😍","🥰","😘","😎","🤩","🥳","🤔",
  "😐","😴","😷","🤒","😢","😭","😠","😡","😨","😱","😲","🙄","😜","🤗","🤝","👍",
  "👎","👏","🙌","💪","🙏","👋","✌️","👌","☝️","✋","❤️","🧡","💛","💚","💙","💜",
  "🖤","⭐","🌟","✨","🔥","💥","🎉","🎈","🎁","🎨","🎵","🎶","📚","📖","✏️","🖊️",
  "📝","📌","📎","🔑","💡","🔔","⏰","📅","💰","💵","💳","🏦","🏠","🏫","🏥","🏪",
  "🚗","🚌","🚕","✈️","🚀","🚲","⛵","🐶","🐱","🐰","🐭","🐻","🐼","🦁","🐯","🐨",
  "🐮","🐷","🐸","🐵","🐔","🐦","🦆","🦅","🐟","🐠","🐢","🐍","🦋","🐝","🐞","🌳",
  "🌲","🌴","🌵","🌸","🌻","🌹","🌷","🍀","🌎","🌙","☀️","⛅","☁️","🌧️","🌈","💧",
  "❄️","🍎","🍌","🍇","🍓","🍉","🍕","🍔","🍟","🍦","🍰","☕","⚽","🏀","🎮","📱",
  "💻","🔴","🟠","🟡","🟢","🔵","🟣","⚫","⚪",
];

export const MindNode = memo(({ id, data, selected }) => {
  const ctx = useContext(MindMapContext);
  const kids = !!ctx?.kids;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(data.label);
  const [menu, setMenu] = useState(null); // null | "emoji" | "shape"
  const [anchor, setAnchor] = useState(null);
  const ref = useRef(null);

  const openMenu = (kind, e) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setAnchor({ top: r.top, bottom: r.bottom, left: r.left });
    setMenu((m) => (m === kind ? null : kind));
  };

  useEffect(() => setText(data.label), [data.label]);
  useEffect(() => {
    if (editing && ref.current) { ref.current.focus(); ref.current.select(); }
  }, [editing]);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = (e) => { if (e.key === "Escape") setMenu(null); };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  const commit = () => {
    setEditing(false);
    if (text !== data.label) ctx.updateLabel(id, text);
  };

  const isRoot = data.isRoot;
  const shape = data.shape || "rounded";
  const centered = shape !== "rounded";

  const colorCls = isRoot
    ? "bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-600/30"
    : data.color
      ? COLOR_STYLES[data.color] || COLOR_STYLES.neutral
      : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100";

  const shapeCls =
    shape === "pill" ? "rounded-full px-6"
    : shape === "circle" ? "rounded-full justify-center"
    : shape === "oval" ? "rounded-[50%] px-8"
    : shape === "cloud" ? ""
    : "rounded-xl";

  const bodyStyle = {};
  if (shape === "cloud") bodyStyle.borderRadius = CLOUD_RADIUS;
  if (shape === "circle") {
    const s = kids ? 168 : 148;
    bodyStyle.width = s; bodyStyle.height = s;
  }

  const showDate = shape === "rounded" && !kids && data.createdAt;
  const btn = `rounded-md ${kids ? "p-2" : "p-1"} hover:bg-black/10 dark:hover:bg-white/10 nodrag`;
  const isz = kids ? 17 : 13;

  return (
    <div className="relative group" data-testid={`mindnode-${id}`}>
      <div
        className={`border backdrop-blur-sm transition-all flex flex-col gap-1.5 ${kids ? "p-4" : "p-3"} ${colorCls} ${shapeCls}
          ${centered ? "items-center justify-center text-center" : ""}
          ${shape === "circle" ? "" : kids ? "min-w-[170px] max-w-[280px]" : "min-w-[150px] max-w-[260px]"}
          ${selected ? "ring-2 ring-indigo-400 ring-offset-2 ring-offset-white dark:ring-offset-slate-950" : ""}`}
        style={bodyStyle}
      >
        <Handle type="target" position={Position.Left} className="!bg-indigo-400 !w-2 !h-2 !border-0" />
        <Handle type="source" position={Position.Right} className="!bg-indigo-400 !w-2 !h-2 !border-0" />

        {data._hasChildren && (
          <button
            onClick={(e) => { e.stopPropagation(); ctx.toggleCollapse(id); }}
            className="nodrag absolute top-1/2 -right-3.5 -translate-y-1/2 z-20 flex items-center gap-0.5 h-6 min-w-[24px] px-1 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white shadow-md border-2 border-white dark:border-slate-950"
            title={data._collapsed ? `Expandir (${data._hiddenCount} ramas ocultas)` : "Colapsar ramas"}
            data-testid={`mindnode-collapse-${id}`}
          >
            {data._collapsed ? <Plus size={13} /> : <Minus size={13} />}
            {data._collapsed && data._hiddenCount > 0 && <span className="font-mono2 text-[10px] font-bold pr-0.5">{data._hiddenCount}</span>}
          </button>
        )}

        {data.emoji && <span className={`leading-none ${kids || centered ? "text-4xl" : "text-2xl"}`}>{data.emoji}</span>}

        {editing ? (
          <textarea
            ref={ref}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); } }}
            className="w-full bg-white text-slate-900 rounded-md border border-slate-300 p-1.5 font-body text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400 nodrag"
            rows={2}
            data-testid={`mindnode-edit-${id}`}
          />
        ) : (
          <div
            className={`font-heading break-words cursor-text leading-snug ${kids ? "font-bold text-lg" : "font-medium text-sm"}`}
            onDoubleClick={() => setEditing(true)}
            data-testid={`mindnode-label-${id}`}
          >
            {data.label || "..."}
          </div>
        )}

        {showDate && (
          <span className={`font-mono2 text-[9px] ${isRoot ? "text-indigo-100" : "text-slate-400 dark:text-slate-500"}`}>
            {format(new Date(data.createdAt), "d MMM yyyy", { locale: es })}
          </span>
        )}

        {data.link && (() => {
          const m = LINK_META[data.link.type] || LINK_META.note;
          return (
            <button
              onClick={(e) => { e.stopPropagation(); ctx.openLink(id); }}
              className={`nodrag mt-1 inline-flex items-center gap-1.5 self-start rounded-md border px-2 py-1 text-[11px] font-medium ${m.cls} hover:brightness-110`}
              title={`Abrir ${m.label}`}
              data-testid={`mindnode-openlink-${id}`}
            >
              <m.Icon size={12} /> {m.label} <ExternalLink size={10} />
            </button>
          );
        })()}
      </div>

      {/* Barra flotante de acciones: visible al seleccionar/tocar o al hover; puente para el hueco */}
      <div className={`absolute -top-10 left-1/2 -translate-x-1/2 items-center gap-0.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-1 py-0.5 shadow-lg nodrag z-10 before:content-[''] before:absolute before:-bottom-2 before:left-0 before:right-0 before:h-2 ${selected ? "flex" : "hidden group-hover:flex"}`}>
        <button onClick={(e) => openMenu("emoji", e)} className={btn} title="Emoji" data-testid={`mindnode-emoji-${id}`}><Smile size={isz} /></button>
        <button onClick={(e) => openMenu("shape", e)} className={btn} title="Forma" data-testid={`mindnode-shape-${id}`}><Shapes size={isz} /></button>
        <button onClick={(e) => openMenu("link", e)} className={`${btn} ${data.link ? "text-indigo-500" : ""}`} title="Vincular formato" data-testid={`mindnode-link-${id}`}><Link2 size={isz} /></button>
        <button onClick={(e) => { e.stopPropagation(); ctx.openHistory(id); }} className={btn} title="Historial" data-testid={`mindnode-history-${id}`}><Clock size={isz} /></button>
        <button onClick={(e) => { e.stopPropagation(); ctx.addChild(id); }} className={`${btn} text-emerald-600 dark:text-emerald-400`} title="Añadir rama" data-testid={`mindnode-add-${id}`}><Plus size={isz} /></button>
        {!isRoot && (
          <button onClick={(e) => { e.stopPropagation(); ctx.removeNode(id); }} className={`rounded-md ${kids ? "p-2" : "p-1"} hover:bg-rose-500/20 text-rose-500 nodrag`} title="Eliminar" data-testid={`mindnode-delete-${id}`}><Trash2 size={isz} /></button>
        )}
      </div>

      {menu && anchor && createPortal(
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setMenu(null)} data-testid="picker-overlay" />
          <div
            className="fixed z-[100] flex flex-col"
            style={(() => {
              const w = menu === "emoji" ? 256 : menu === "link" ? 208 : 140;
              const left = Math.min(Math.max(8, anchor.left - w / 2), window.innerWidth - w - 8);
              const belowSpace = window.innerHeight - anchor.bottom - 12;
              const aboveSpace = anchor.top - 12;
              const openUp = belowSpace < 220 && aboveSpace > belowSpace;
              const st = { left, width: w, maxHeight: Math.max(160, openUp ? aboveSpace : belowSpace) };
              if (openUp) st.bottom = window.innerHeight - anchor.top + 6;
              else st.top = anchor.bottom + 6;
              return st;
            })()}
          >
            {menu === "emoji" ? (
              <div className="flex flex-col min-h-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden" data-testid={`emoji-picker-${id}`}>
                <button onClick={() => { ctx.setEmoji(id, ""); setMenu(null); }} className="text-[11px] text-slate-400 hover:text-rose-500 flex items-center justify-center gap-1 py-1.5 border-b border-slate-100 dark:border-slate-800 shrink-0"><X size={11} /> Quitar emoji</button>
                <div className="grid grid-cols-8 gap-1 overflow-y-auto p-2 min-h-0">
                  {EMOJIS.map((em) => (
                    <button key={em} onClick={() => { ctx.setEmoji(id, em); setMenu(null); }} className="text-xl hover:scale-125 transition-transform" data-testid={`emoji-opt-${em}`}>{em}</button>
                  ))}
                </div>
              </div>
            ) : menu === "link" ? (
              <div className="flex flex-col gap-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-1.5 shadow-xl overflow-y-auto min-h-0" data-testid={`link-picker-${id}`}>
                {data.link ? (
                  <>
                    <button onClick={() => { ctx.openLink(id); setMenu(null); }} className="text-left text-xs px-2 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2" data-testid={`link-open-${id}`}><ExternalLink size={13} /> Abrir {LINK_META[data.link.type]?.label}</button>
                    <button onClick={() => { ctx.removeLink(id); setMenu(null); }} className="text-left text-xs px-2 py-1.5 rounded-md hover:bg-rose-500/10 text-rose-500 flex items-center gap-2" data-testid={`link-remove-${id}`}><X size={13} /> Quitar vínculo</button>
                  </>
                ) : (
                  <>
                    <p className="text-[10px] text-slate-400 px-2 pt-1 pb-0.5">Crear y vincular formato:</p>
                    <button onClick={() => { ctx.linkFormat(id, "note"); setMenu(null); }} className="text-left text-xs px-2 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2" data-testid={`link-new-note-${id}`}><FileText size={13} className="text-sky-500" /> Nota</button>
                    <button onClick={() => { ctx.linkFormat(id, "sheet"); setMenu(null); }} className="text-left text-xs px-2 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2" data-testid={`link-new-sheet-${id}`}><Table2 size={13} className="text-emerald-500" /> Hoja de cálculo</button>
                    <button onClick={() => { ctx.linkFormat(id, "movemap"); setMenu(null); }} className="text-left text-xs px-2 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2" data-testid={`link-new-movemap-${id}`}><Wallet size={13} className="text-orange-500" /> Mapa de flujo económico</button>
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-1.5 shadow-xl overflow-y-auto min-h-0" data-testid={`shape-picker-${id}`}>
                {SHAPES.map((s) => (
                  <button key={s.id} onClick={() => { ctx.setShape(id, s.id); setMenu(null); }} className={`text-left text-xs px-2 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 ${shape === s.id ? "bg-indigo-50 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-300 font-medium" : ""}`} data-testid={`shape-opt-${s.id}`}>{s.label}</button>
                ))}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
});

MindNode.displayName = "MindNode";
